/**
 * Identifies responses that were produced by an intermediary rather than by the
 * Finster server.
 *
 * Every error the add-in server returns is JSON (`sendJsonError`), so a
 * non-JSON error body means something between the client and Node answered
 * instead: Cloudflare, the Google load balancer, or a corporate proxy or DLP
 * appliance. Those blocks never reach server-side logging, so the client is the
 * only place the evidence exists.
 *
 * Header evidence is deliberately ranked below body evidence, and only headers
 * an intermediary cannot have set on someone else's behalf are used to name a
 * blocker. Cloudflare proxies every response for the add-in hostname: it stamps
 * `cf-ray` on all of them and rewrites `Server` to `cloudflare`, including on
 * origin errors it merely relayed. `Server: Google Frontend` is the exception —
 * surviving that rewrite means Cloudflare did not produce the response.
 *
 * An unattributable block is reported as `unknown` rather than guessed at, since
 * the whole point is to send an operator to the right intermediary. The raw
 * headers and body preview travel with the diagnosis so the call can be made by
 * hand from the logs.
 */

const CLOUDFLARE_BODY_SIGNALS = [
  "attention required",
  "sorry, you have been blocked",
  "cloudflare ray id",
  "error code: 1020",
  "cloudflare",
];

const GOOGLE_BODY_SIGNALS = [
  "your client does not have permission",
  "google frontend",
];

const BODY_PREVIEW_LENGTH = 300;

export type NetworkBlockSource = "cloudflare" | "google_frontend" | "proxy" | "unknown";

export interface NetworkBlockDiagnosis {
  source: NetworkBlockSource;
  status: number;
  cfRay: string | null;
  cfMitigated: string | null;
  server: string | null;
  via: string | null;
  bodyPreview: string;
}

function isJsonBody(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return false;
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    return false;
  }
}

function summarizeBody(text: string): string {
  return text.replace(/\s+/g, " ").trim().slice(0, BODY_PREVIEW_LENGTH);
}

function classify(
  bodyText: string,
  headers: { cfMitigated: string | null; server: string | null; via: string | null }
): NetworkBlockSource {
  const body = bodyText.toLowerCase();
  const server = headers.server?.toLowerCase() ?? "";

  if (CLOUDFLARE_BODY_SIGNALS.some((signal) => body.includes(signal))) return "cloudflare";
  if (GOOGLE_BODY_SIGNALS.some((signal) => body.includes(signal))) return "google_frontend";
  // Set only when Cloudflare itself challenged or blocked the request, unlike
  // `cf-ray` and `Server`, which ride along on everything it proxies.
  if (headers.cfMitigated) return "cloudflare";
  if (server.includes("google frontend")) return "google_frontend";
  if (headers.via) return "proxy";
  return "unknown";
}

/**
 * Returns a diagnosis when an intermediary produced the response, or `null`
 * when the body is JSON and therefore came from the Finster server.
 */
export function diagnoseBlockedResponse(
  response: Pick<Response, "ok" | "status" | "headers">,
  bodyText: string
): NetworkBlockDiagnosis | null {
  if (response.ok) return null;
  if (isJsonBody(bodyText)) return null;

  const headers = {
    cfMitigated: response.headers.get("cf-mitigated"),
    server: response.headers.get("server"),
    via: response.headers.get("via"),
  };

  return {
    source: classify(bodyText, headers),
    status: response.status,
    cfRay: response.headers.get("cf-ray"),
    cfMitigated: headers.cfMitigated,
    server: headers.server,
    via: headers.via,
    bodyPreview: summarizeBody(bodyText),
  };
}

/**
 * User-facing sentence.
 *
 * It names neither this product nor the intermediary. The add-in is
 * white-labelled, so a deployment's users must never be shown "Finster", and
 * which CDN or appliance refused the upload is an operator concern rather than
 * something the person importing a deck can act on. `source` and the raw
 * headers travel on the client-log event for that.
 *
 * The reference id is worth showing, because it is what a support request is
 * traced by, and it identifies nothing on its own.
 */
export function describeNetworkBlock(diagnosis: NetworkBlockDiagnosis): string {
  const reference = diagnosis.cfRay ? ` (reference ${diagnosis.cfRay})` : "";
  return `Upload was blocked by network security before it reached the service${reference}. HTTP ${diagnosis.status}.`;
}
